
import { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import AppLayout from "../layout/AppLayout";
import Loader from "../loader/loader";
import regenerateToken from "../regenerateToken";
import { buildRequestConfig } from "../../utils/apiHelper";
import { useAdmin } from "../layout/AdminContext";
import {
  FaUserCircle,
  FaBuilding,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaChartLine,
  FaCalendarAlt,
  FaDatabase,
  FaShieldAlt,
  FaRobot,
  FaVideo,
  FaMobileAlt,
  FaLanguage,
  FaPaintBrush,
  FaEdit,
  FaLayerGroup,
  FaUsersCog,
  FaSearch,
  FaSpinner,
  FaCrown,
  FaIdBadge,
  FaEnvelope,
  FaTag,
  FaTicketAlt
} from "react-icons/fa";
import "./homePage.css";


/** Helper: Parse credentials from localStorage and build safe display data */
const parseCredentials = (parsed) => {
  if (!parsed) return null;

  // personalisedServices & customisation live inside reportSubscription
  // Fallback to top-level for backward-compat with older login responses
  const rs = parsed.reportSubscription || {};

  return {
    accessTo: parsed.accessTo || { portal: true, smartReport: true, dashBoard: true },
    center: parsed.center,
    clientId: parsed.clientId,
    clientType: parsed.clientType,
    customerName: parsed.customerName,
    displayName: parsed.displayName,
    endDate: parsed.endDate,
    endpoints: parsed.endpoints || {},
    logoSrc: parsed.logoSrc,
    notifications: parsed.notifications,
    org: parsed.org,
    planType: parsed.planType,
    reportAddons: parsed.reportAddons,
    reportSubscription: parsed.reportSubscription,
    reportsSubscribed: parsed.reportsSubscribed,
    role: parsed.role,
    serviceType: parsed.serviceType,
    startDate: parsed.startDate,
    subscription: parsed.subscription,
    subscriptionStatus: parsed.subscriptionStatus,
    userId: parsed.userId,
    websiteUrl: parsed.websiteUrl,
    // Read from reportSubscription (primary) → fallback to top-level (legacy)
    customisation: rs.customisation || parsed.customisation,
    personalisedServices: rs.personalisedServices || parsed.personalisedServices,
  };
};

const SUBSCRIPTION_API_BASE = "https://rnxsohimg1.execute-api.ap-south-1.amazonaws.com/Production/subscription/status";

const HomePage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const { isAdmin: adminUser, selectedClient, clientsList, viewMode } = useAdmin();

  // Initialize state from localStorage as immediate fallback
  const [data, setData] = useState(() => {
    const storedStr = localStorage.getItem("credentials");
    if (storedStr) {
      try {
        return parseCredentials(JSON.parse(storedStr));
      } catch (e) {
        console.error("Failed to parse credentials from storage", e);
        return null;
      }
    }
    return null;
  });

  // Subscription data fetched from billing API for the admin-selected client
  const [clientSubData, setClientSubData] = useState(null);
  const [clientSubLoading, setClientSubLoading] = useState(false);
  const [clientSubError, setClientSubError] = useState("");

  const [animateProgress, setAnimateProgress] = useState(0);

  // Fetch fresh user details from API on every homepage load
  const fetchFreshUserData = useCallback(async () => {
    try {
      const idToken = Cookies.get("idToken");
      if (!idToken) {
        console.warn("HomePage: No auth token found, using stored data.");
        return;
      }

      const stateConfigUrl = process.env.REACT_APP_STATE_CONFIG_URL;
      if (!stateConfigUrl) {
        console.warn("HomePage: REACT_APP_STATE_CONFIG_URL not configured.");
        return;
      }

      let response = await axios(buildRequestConfig({
        method: "GET",
        url: stateConfigUrl,
      }));

      // Handle token expiry – regenerate and retry once
      if (response.status === 401) {
        await regenerateToken();
        const freshToken = Cookies.get("idToken");
        if (!freshToken) return;
        response = await axios(buildRequestConfig({
          method: "GET",
          url: stateConfigUrl,
        }));
      }

      const apiData = response?.data?.data || {};

      // If the API response includes personalisedServices / customisation, use them
      // These may be at the top level of apiData or inside a nested key
      const freshPersonalisedServices =
        apiData.personalisedServices || apiData.reportSubscription?.personalisedServices;
      const freshCustomisation =
        apiData.customisation || apiData.reportSubscription?.customisation;

      if (freshPersonalisedServices || freshCustomisation) {
        setData((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            personalisedServices: freshPersonalisedServices || prev.personalisedServices,
            customisation: freshCustomisation || prev.customisation,
          };
        });
        console.log("HomePage: Refreshed add-on data from API", {
          personalisedServices: freshPersonalisedServices,
          customisation: freshCustomisation,
        });
      }
    } catch (err) {
      console.error("HomePage: Error fetching fresh data", err);
    }
  }, []);

  // Fetch subscription data from billing API for selected client
  const fetchClientSubscriptionData = useCallback(async (userId) => {
    if (!userId) return;
    setClientSubLoading(true);
    setClientSubError("");
    setClientSubData(null);
    try {
      const url = `${SUBSCRIPTION_API_BASE}?userId=${encodeURIComponent(userId)}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const json = await response.json();
      const subData = json?.data || null;
      console.log("HomePage: Subscription data for client", userId, subData);
      setClientSubData(subData);
    } catch (err) {
      console.error("HomePage: Failed to fetch client subscription data", err);
      setClientSubError("Could not load subscription details for this client.");
    } finally {
      setClientSubLoading(false);
    }
  }, []);

  useEffect(() => {
    // Admin in 'admin' mode — no client data needed, just unlock loading
    if (adminUser && viewMode === 'admin') {
      setLoading(false);
      return;
    }

    // Admin in 'client' mode but no client selected yet
    if (adminUser && viewMode === 'client' && !selectedClient) {
      setLoading(false);
      return;
    }

    // Admin in 'client' mode WITH a selected client — fetch their subscription data
    if (adminUser && viewMode === 'client' && selectedClient) {
      fetchClientSubscriptionData(selectedClient.userId);
      setLoading(false);
      setTimeout(() => setAnimateProgress(1), 300);
      return;
    }

    // Regular client — redirect if no valid session data
    if (!data || !data.userId) {
      console.warn("No valid session data found. Redirecting to login.");
      history.replace("/login");
      return;
    }

    fetchFreshUserData();

    const handleAdminClientChange = () => { fetchFreshUserData(); };
    window.addEventListener('adminClientChanged', handleAdminClientChange);

    setTimeout(() => {
      setLoading(false);
      setTimeout(() => setAnimateProgress(1), 100);
    }, 800);

    return () => {
      window.removeEventListener('adminClientChanged', handleAdminClientChange);
    };
  }, [data, history, fetchFreshUserData, adminUser, selectedClient, viewMode, fetchClientSubscriptionData]);

  // --- Helper Logic ---
  const calculateDaysRemaining = (end) => {
    if (!end) return 0;
    const endDate = new Date(end);
    const today = new Date();
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const calculateProgress = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start).getTime();
    const endDate = new Date(end).getTime();
    const today = new Date().getTime();

    if (today < startDate) return 0;
    if (today > endDate) return 100;

    const total = endDate - startDate;
    const current = today - startDate;
    return Math.min(Math.round((current / total) * 100), 100);
  };

  // When admin has a selected client, use clientSubData for the dashboard display
  // Otherwise fall back to `data` (regular user credentials or null)
  const activeData = (adminUser && selectedClient) ? clientSubData : data;

  const daysLeft = calculateDaysRemaining(activeData?.endDate);
  const planProgress = calculateProgress(activeData?.startDate, activeData?.endDate);
  const displayProgress = animateProgress ? planProgress : 0; // Use animation state
  const isLive = activeData?.subscriptionStatus === "live";

  // Configuration for Personalized Services icons
  const serviceIcons = {
    analyticsDashboard: { label: 'Analytics Dashboard', icon: <FaChartLine /> },
    whatsappChatbot: { label: 'WhatsApp Chatbot', icon: <FaRobot /> },
    languageSupport: { label: 'Language Support', icon: <FaLanguage /> },
    videoreport: { label: 'Video Report', icon: <FaVideo /> },
    vizapp: { label: 'VizApp', icon: <FaMobileAlt /> }
  };

  if (loading) return <AppLayout><Loader /></AppLayout>;

  // --- Admin Mode Home: admin-level tools, no client needed ---
  if (adminUser && viewMode === 'admin') {
    return (
      <AppLayout>
        <div className="admin-mode-home px-4 py-4">

          {/* Hero */}
          <div className="admin-mode-hero">
            <div className="admin-mode-hero-text">
              <div className="admin-mode-badge"><FaShieldAlt /> Admin Mode</div>
              <h1 className="admin-mode-title">Admin Control Panel</h1>
              <p className="admin-mode-subtitle">
                You are operating at the <strong>admin level</strong> — no client is loaded.
                All mappings and configurations here apply globally without a client context.
              </p>
            </div>
            <div className="admin-mode-hero-visual">
              <div className="admin-hero-ring">
                <FaShieldAlt size={52} className="admin-hero-shield" />
              </div>
            </div>
          </div>

          {/* Quick Access Cards */}
          <h2 className="admin-quickaccess-title">Quick Access — Admin Tools</h2>
          <div className="admin-quickaccess-grid">
            <div className="admin-qa-card" onClick={() => history.push('/profile-mapping')} role="button" tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && history.push('/profile-mapping')}>
              <div className="admin-qa-icon" style={{ background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                <FaUserCircle size={26} />
              </div>
              <div className="admin-qa-label">Profile Mapping</div>
              <div className="admin-qa-desc">View and manage admin-level profile mappings</div>
              <FaExternalLinkAlt className="admin-qa-arrow" />
            </div>

            <div className="admin-qa-card" onClick={() => history.push('/parameter-mapping')} role="button" tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && history.push('/parameter-mapping')}>
              <div className="admin-qa-icon" style={{ background: 'linear-gradient(135deg, #0891b2, #0e7490)' }}>
                <FaDatabase size={26} />
              </div>
              <div className="admin-qa-label">Parameter Mapping</div>
              <div className="admin-qa-desc">Manage parameter mappings at the admin level</div>
              <FaExternalLinkAlt className="admin-qa-arrow" />
            </div>

            <div className="admin-qa-card" onClick={() => history.push('/unmapped-management')} role="button" tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && history.push('/unmapped-management')}>
              <div className="admin-qa-icon" style={{ background: 'linear-gradient(135deg, #d97706, #b45309)' }}>
                <FaShieldAlt size={26} />
              </div>
              <div className="admin-qa-label">Parameter Mapping Auto</div>
              <div className="admin-qa-desc">Auto-detect and resolve unmapped parameters</div>
              <FaExternalLinkAlt className="admin-qa-arrow" />
            </div>

            <div className="admin-qa-card" onClick={() => history.push('/ordering')} role="button" tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && history.push('/ordering')}>
              <div className="admin-qa-icon" style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
                <FaLayerGroup size={26} />
              </div>
              <div className="admin-qa-label">Profile/Param Ordering</div>
              <div className="admin-qa-desc">Set global ordering for profiles and parameters</div>
              <FaExternalLinkAlt className="admin-qa-arrow" />
            </div>
          </div>

          {/* Client count info */}
          {clientsList.length > 0 && (
            <div className="admin-mode-client-hint">
              <FaUsersCog className="admin-mode-hint-icon" />
              <span>
                <strong>{clientsList.length} clients</strong> are available —
                switch to <strong>Client Mode</strong> from the sidebar toggle to configure a specific client.
              </span>
            </div>
          )}
        </div>
      </AppLayout>
    );
  }

  // --- Client Mode: Admin with no client selected yet ---
  if (adminUser && viewMode === 'client' && !selectedClient) {
    return (
      <AppLayout>
        <div className="admin-welcome-container px-4 py-4">

          <div className="admin-glass-hero mb-4">
            <div className="admin-hero-content">
              <div className="admin-hero-icon-wrapper">
                <FaUsersCog size={42} className="admin-hero-icon" />
              </div>
              <h1 className="admin-hero-title">Admin Control Panel</h1>
              <p className="admin-hero-subtitle">
                Welcome back to your central command. You are currently in Super Admin mode.
                Select a client from the sidebar on the left to securely configure their portal layout, reports, and mappings.
              </p>
            </div>
            <div className="admin-hero-visual">
              <div className="floating-illustration">
                <div className="floating-card front-card">
                  <div className="card-line w-50"></div>
                  <div className="card-line w-75"></div>
                  <FaUsersCog size={40} className="card-icon" />
                </div>
                <div className="floating-card back-card">
                  <FaBuilding size={32} className="card-icon text-white op-50" />
                  <div className="card-line w-75 bg-light op-50"></div>
                </div>
              </div>
            </div>
          </div>

          {!clientsList.length ? (
            <div className="col-12 py-5">
              <div className="admin-glass-empty p-5 text-center mx-auto" style={{ maxWidth: '500px' }}>
                <FaUsersCog size={48} className="text-secondary opacity-50 mb-3" />
                <h4 className="fw-bold mb-3 text-dark">No clients found in session</h4>
                <p className="text-muted mx-auto mb-4" style={{ lineHeight: '1.6' }}>
                  If you recently logged in before the update, or just gained Admin privileges, your session might be missing the client list stream.
                </p>
                <button
                  className="btn btn-primary px-4 py-2 rounded-pill shadow-sm"
                  onClick={() => {
                    localStorage.removeItem("credentials");
                    sessionStorage.removeItem("adminSelectedClient");
                    Cookies.remove("idToken", { path: '/' });
                    history.replace("/login");
                  }}
                >
                  Log Out Now
                </button>
              </div>
            </div>
          ) : (
            <div className="admin-info-card mt-3">
              <h4><FaSearch className="me-3" /> How to configure a client</h4>
              <div className="admin-info-steps text-start mt-4">
                <div className="admin-step">
                  <div className="admin-step-num">1</div>
                  <div className="admin-step-text">Use the search bar in the left navigation panel to find a specific client by ID or Organization Name.</div>
                </div>
                <div className="admin-step">
                  <div className="admin-step-num">2</div>
                  <div className="admin-step-text">Click on the client to securely load their isolated configuration context into your session.</div>
                </div>
                <div className="admin-step">
                  <div className="admin-step-num">3</div>
                  <div className="admin-step-text">Navigate through the portal tabs (Reports, Parameters, etc.) to view and modify their unique settings.</div>
                </div>
              </div>
            </div>
          )}

        </div>
      </AppLayout>
    );
  }

  // --- Admin Client Dashboard (client mode + client selected → show their subscription data) ---
  if (adminUser && viewMode === 'client' && selectedClient) {
    if (clientSubLoading) {
      return (
        <AppLayout>
          <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '60vh', gap: '16px' }}>
            <div className="admin-sub-spinner">
              <div className="spinner-ring"></div>
            </div>
            <p className="text-muted fw-semibold" style={{ fontSize: '1rem' }}>
              Loading subscription data for <strong>{selectedClient.org} / {selectedClient.center}</strong>...
            </p>
          </div>
        </AppLayout>
      );
    }

    if (clientSubError || !clientSubData) {
      return (
        <AppLayout>
          <div className="dashboard-container px-4 py-4">
            <div className="admin-client-error-banner">
              <FaTimesCircle size={32} className="text-danger mb-2" />
              <h4 className="fw-bold mb-1">{clientSubError || "No subscription data found"}</h4>
              <p className="text-muted mb-0 small">
                Could not retrieve details for <strong>{selectedClient.org} / {selectedClient.center}</strong> (userId: {selectedClient.userId}).
                The client may not be registered in the billing system.
              </p>
            </div>
          </div>
        </AppLayout>
      );
    }

    const sub = clientSubData;
    const subIsLive = sub.subscriptionStatus === "live";
    const subDaysLeft = calculateDaysRemaining(sub.endDate);
    const subProgress = calculateProgress(sub.startDate, sub.endDate);
    const subDisplayProgress = animateProgress ? subProgress : 0;
    const subMailIds = sub.notifications?.mailIds || [];

    return (
      <AppLayout>
        <div className="dashboard-container px-4 py-4">

          {/* Hero Banner — Client Identity */}
          <div className="hero-banner" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4f46e5 100%)' }}>
            <div className="hero-content">
              <div className="admin-client-avatar">
                <FaCrown size={28} className="admin-crown-icon" />
              </div>
              <div className="hero-text">
                <div className="admin-viewing-badge mb-2">
                  <FaUsersCog size={11} /> Admin view
                </div>
                <h1>{sub.customerName || sub.org || selectedClient.org}</h1>
                <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                  <span className="client-id-badge">
                    <span className="opacity-75">Client ID:</span> {sub.clientId || selectedClient.clientId}
                  </span>
                  <span className="client-id-badge">
                    <span className="opacity-75">User ID:</span> {sub.userId || selectedClient.userId}
                  </span>
                  <span className="client-id-badge">
                    <span className="opacity-75">Center:</span> {sub.center || selectedClient.center}
                  </span>
                </div>
              </div>
            </div>
            <div className="hero-actions d-none d-md-flex align-items-center gap-4">
              <div className="hero-endpoint-status">
                <div className="small opacity-75">Subscription</div>
                <div className={subIsLive ? 'fw-bold' : 'fw-bold text-muted'} style={{ color: subIsLive ? '#6ee7b7' : undefined }}>
                  {sub.subscriptionStatus?.toUpperCase() || 'UNKNOWN'}
                </div>
              </div>
            </div>
            <div className="hero-bg-accent"></div>
          </div>

          {/* Key Metrics */}
          <div className="row g-4 mb-4">

            {/* Plan / Subscription */}
            <div className="col-12 col-md-6 col-xl-4">
              <div className="stat-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="card-label">Current Plan</div>
                  <div className={`sub-status ${subIsLive ? 'live' : ''}`}>
                    {subIsLive ? <FaCheckCircle /> : <FaTimesCircle />}
                    {sub.subscriptionStatus?.toUpperCase()}
                  </div>
                </div>
                <div className="card-value mb-1 text-capitalize">{sub.subscription || sub.reportSubscription?.reportType || '–'}</div>
                <div className="small text-muted mb-3">
                  Expires: {sub.endDate ? new Date(sub.endDate).toLocaleDateString() : 'N/A'}
                </div>
                <div className="progress-container">
                  <div className="progress-label">
                    <span>Usage Period</span>
                    <span>{subDaysLeft} days left</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${subDisplayProgress}%` }}></div>
                  </div>
                </div>
                <FaCalendarAlt className="stat-icon-bg" />
              </div>
            </div>

            {/* Reports Allowance */}
            <div className="col-12 col-md-6 col-xl-4">
              <div className="stat-card">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="card-label">Total Reports Allowance</div>
                  <div className="badge bg-light text-primary border rounded-pill px-3 py-2 d-flex align-items-center gap-2">
                    <FaLayerGroup size={12} />
                    <span className="text-capitalize fw-bold">{sub.reportSubscription?.reportType || '–'}</span>
                  </div>
                </div>
                <div className="card-value text-primary display-5 fw-bold mb-1">
                  {sub.reportsSubscribed ?? 0}
                </div>
                <div className="text-muted small opacity-75">Maximum reports generation limit</div>
                <FaDatabase className="stat-icon-bg" />
              </div>
            </div>

            {/* Organization Details */}
            <div className="col-12 col-md-6 col-xl-4">
              <div className="stat-card">
                <div className="card-label">Organization Details</div>
                <div className="d-flex flex-column gap-2 mt-2">
                  <div className="d-flex align-items-center gap-2">
                    <FaBuilding className="text-muted" />
                    <span className="fw-bold">{sub.org}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <FaShieldAlt className="text-muted" />
                    <span>Center: {sub.center}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <FaIdBadge className="text-muted" />
                    <span className="text-truncate" style={{ maxWidth: '180px' }}>ID: {sub.clientId}</span>
                  </div>
                </div>
                <FaBuilding className="stat-icon-bg" />
              </div>
            </div>
          </div>

          {/* Second row of detail cards */}
          <div className="row g-4 mb-4">

            {/* Access Permissions */}
            {sub.accessTo && (
              <div className="col-12 col-md-6">
                <div className="stat-card">
                  <div className="card-label mb-3">Access Permissions</div>
                  <div className="d-flex flex-column gap-2">
                    {[
                      { label: 'Dashboard', val: sub.accessTo.dashBoard },
                      { label: 'Portal', val: sub.accessTo.portal },
                      { label: 'Smart Report', val: sub.accessTo.smartReport },
                    ].map(({ label, val }) => (
                      <div key={label} className="d-flex align-items-center justify-content-between">
                        <span className="text-muted small fw-semibold">{label}</span>
                        <span className={`badge rounded-pill px-3 py-2 ${val ? 'bg-success bg-opacity-15 text-success' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
                          {val ? <><FaCheckCircle size={10} className="me-1" />Yes</> : <><FaTimesCircle size={10} className="me-1" />No</>}
                        </span>
                      </div>
                    ))}
                  </div>
                  <FaShieldAlt className="stat-icon-bg" />
                </div>
              </div>
            )}

            {/* Plan Info */}
            <div className="col-12 col-md-6">
              <div className="stat-card">
                <div className="card-label mb-3">Plan & Service Details</div>
                <div className="d-flex flex-column gap-2">
                  {[
                    { label: 'Plan Type', val: sub.planType || '–', icon: <FaCrown size={12} className="text-warning" /> },
                    { label: 'Service Type', val: sub.serviceType || '–', icon: <FaTag size={12} className="text-info" /> },
                    { label: 'Client Type', val: sub.clientType || '–', icon: <FaIdBadge size={12} className="text-primary" /> },
                    { label: 'Tickets', val: sub.numberOfTickets != null ? sub.numberOfTickets : '–', icon: <FaTicketAlt size={12} className="text-secondary" /> },
                  ].map(({ label, val, icon }) => (
                    <div key={label} className="d-flex align-items-center justify-content-between">
                      <span className="text-muted small fw-semibold d-flex align-items-center gap-1">{icon}{label}</span>
                      <span className="fw-bold small text-dark">{val}</span>
                    </div>
                  ))}
                </div>
                <FaLayerGroup className="stat-icon-bg" />
              </div>
            </div>
          </div>

          {/* Notification Emails */}
          {subMailIds.length > 0 && (
            <div className="config-section mt-0">
              <div className="section-title">
                <FaEnvelope className="text-primary" />
                Notification Emails
              </div>
              <div className="tags-container">
                {subMailIds.map((mail, i) => (
                  <div className="feature-tag active" key={i}>
                    <FaCheckCircle size={12} /> {mail}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Report Add-ons */}
          {sub.reportAddons && sub.reportAddons.length > 0 && (
            <div className="config-section mt-4">
              <div className="section-title">
                <FaLayerGroup className="text-success" />
                Report Add-ons
              </div>
              <div className="tags-container">
                {sub.reportAddons.map((addon, i) => (
                  <div className="feature-tag active" key={i}>
                    <FaCheckCircle size={12} /> {addon}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Subscription Date Range */}
          <div className="row g-4 mt-0">
            <div className="col-12">
              <div className="admin-sub-dates-card">
                <div className="admin-sub-dates-item">
                  <div className="admin-sub-dates-label">Start Date</div>
                  <div className="admin-sub-dates-value">{sub.startDate ? new Date(sub.startDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '–'}</div>
                </div>
                <div className="admin-sub-dates-divider"></div>
                <div className="admin-sub-dates-item">
                  <div className="admin-sub-dates-label">End Date</div>
                  <div className="admin-sub-dates-value">{sub.endDate ? new Date(sub.endDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' }) : '–'}</div>
                </div>
                <div className="admin-sub-dates-divider"></div>
                <div className="admin-sub-dates-item">
                  <div className="admin-sub-dates-label">Days Remaining</div>
                  <div className="admin-sub-dates-value" style={{ color: subDaysLeft < 30 ? '#e53e3e' : '#38a169' }}>{subDaysLeft} days</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </AppLayout>
    );
  }

  // --- Regular Client Dashboard (non-admin user) ---
  return (
    <AppLayout>
      <div className="dashboard-container px-4 py-4">

        {/* --- Hero Section --- */}
        <div className="hero-banner">
          <div className="hero-content">
            <div className="client-logo-wrapper">
              <img
                src={activeData?.logoSrc}
                alt="Client Logo"
                className="client-logo"
                onError={(e) => { e.target.src = "https://via.placeholder.com/80?text=Logo" }}
              />
            </div>
            <div className="hero-text">
              <h1>{activeData?.displayName}</h1>
              <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                <span className="client-id-badge">
                  <span className="opacity-75">Client ID:</span> {activeData?.clientId}
                </span>
                <span className={`hero-endpoint-badge d-md-none ${activeData?.endpoints?.smartReport ? 'available' : ''}`}>
                  Endpoint: {activeData?.endpoints?.smartReport ? 'Available' : 'Not available'}
                </span>
                {activeData?.websiteUrl && (
                  <a
                    href={activeData.websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white text-decoration-none small d-flex align-items-center gap-1 opacity-75 hover-opacity-100"
                  >
                    Visit Website <FaExternalLinkAlt size={10} />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="hero-actions d-none d-md-flex align-items-center gap-4">
            {/* Endpoint status */}
            <div className="hero-endpoint-status">
              <div className="small opacity-75">Endpoint</div>
              <div className={activeData?.endpoints?.smartReport ? 'fw-bold text-success' : 'fw-bold text-muted'}>
                {activeData?.endpoints?.smartReport ? 'Available' : 'Not available'}
              </div>
            </div>
            {/* Notification Bell */}
            <div className="d-flex align-items-center gap-3">
              <div className="notif-bell">
                <FaLayerGroup color="white" size={20} />
                {activeData?.notifications?.status === 'new' && <div className="notif-dot"></div>}
              </div>
            </div>
          </div>

          <div className="hero-bg-accent"></div>
        </div>

        {/* --- Key Metrics Grid --- */}
        <div className="row g-4 mb-5">
          {/* Subscription Card */}
          <div className="col-12 col-md-6 col-xl-4">
            <div className="stat-card">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="card-label">Current Plan</div>
                <div className={`sub-status ${isLive ? 'live' : ''}`}>
                  {isLive ? <FaCheckCircle /> : <FaTimesCircle />}
                  {activeData?.subscriptionStatus?.toUpperCase()}
                </div>
              </div>
              <div className="card-value mb-1">{activeData?.subscription}</div>
              <div className="small text-muted mb-3">
                Expires: {activeData?.endDate ? new Date(activeData.endDate).toLocaleDateString() : 'N/A'}
              </div>

              <div className="progress-container">
                <div className="progress-label">
                  <span>Usage Period</span>
                  <span>{daysLeft} days left</span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${displayProgress}%` }}
                  ></div>
                </div>
              </div>

              <FaCalendarAlt className="stat-icon-bg" />
            </div>
          </div>

          {/* Volume Card */}
          <div className="col-12 col-md-6 col-xl-4">
            <div className="stat-card">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="card-label">Total Reports Allowance</div>
                <div className="badge bg-light text-primary border rounded-pill px-3 py-2 d-flex align-items-center gap-2">
                  <FaLayerGroup size={12} />
                  <span className="text-capitalize fw-bold">{activeData?.reportSubscription?.reportType}</span>
                </div>
              </div>

              <div className="card-value text-primary display-5 fw-bold mb-1">
                {activeData?.reportsSubscribed ?? 0}
              </div>
              <div className="text-muted small opacity-75">
                Maximum reports generation limit
              </div>

              <FaDatabase className="stat-icon-bg" />
            </div>
          </div>

          {/* Organization Card */}
          <div className="col-12 col-md-6 col-xl-4">
            <div className="stat-card">
              <div className="card-label">Organization Details</div>
              <div className="d-flex flex-column gap-2 mt-2">
                <div className="d-flex align-items-center gap-2">
                  <FaBuilding className="text-muted" />
                  <span className="fw-bold">{activeData?.org}</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <FaUserCircle className="text-muted" />
                  <span>{activeData?.role} Account</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <FaShieldAlt className="text-muted" />
                  <span>Center: {activeData?.center}</span>
                </div>
              </div>
              <FaBuilding className="stat-icon-bg" />
            </div>
          </div>
        </div>

        {/* --- New Sections Row --- */}
        <div className="row g-4">

          {/* Personalized Services */}
          {activeData?.personalisedServices != null && typeof activeData.personalisedServices === "object" && (
            <div className="col-12 col-md-6">
              <div className="config-section h-100 mt-0">
                <div className="section-title">
                  <FaRobot className="text-primary" />
                  Personalized Services
                </div>
                <div className="tags-container">
                  {Object.keys(activeData.personalisedServices).length === 0 ? (
                    <span className="text-muted small">No items in response</span>
                  ) : (
                    Object.entries(activeData.personalisedServices).map(([key, hasAccess]) => {
                      const meta = serviceIcons[key];
                      const label = meta ? meta.label : key;
                      return (
                        <div
                          className={`feature-tag ${hasAccess ? "active" : "no-access"}`}
                          key={key}
                          title={hasAccess ? "You have access" : "No access"}
                        >
                          {hasAccess ? (
                            meta ? meta.icon : <FaCheckCircle size={12} />
                          ) : (
                            <FaTimesCircle size={12} />
                          )}
                          {label}
                          <span className="access-badge">{hasAccess ? "Access" : "No access"}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Customization Capabilities */}
          {activeData?.customisation != null && typeof activeData.customisation === "object" && (
            <div className="col-12 col-md-6">
              <div className="config-section h-100 mt-0">
                <div className="section-title">
                  <FaEdit className="text-warning" />
                  Customization Capabilities
                </div>
                <div className="tags-container">
                  {[
                    {
                      key: "profile",
                      label: "Profile Content",
                      icon: <FaUserCircle size={12} />,
                      hasAccess: activeData.customisation.content && typeof activeData.customisation.content.profile === "boolean"
                        ? activeData.customisation.content.profile
                        : activeData.customisation.content?.profile === true,
                    },
                    {
                      key: "parameter",
                      label: "Parameter Content",
                      icon: <FaDatabase size={12} />,
                      hasAccess: activeData.customisation.content && typeof activeData.customisation.content.parameter === "boolean"
                        ? activeData.customisation.content.parameter
                        : activeData.customisation.content?.parameter === true,
                    },
                    {
                      key: "design",
                      label: "Design & Layout",
                      icon: <FaPaintBrush size={12} />,
                      hasAccess: activeData.customisation.design === true,
                    },
                  ].map(({ key, label, icon, hasAccess }) => (
                    <div
                      className={`feature-tag ${hasAccess ? "active" : "no-access"}`}
                      key={key}
                      title={hasAccess ? "You have access" : "No access"}
                    >
                      {hasAccess ? icon : <FaTimesCircle size={12} />}
                      {label}
                      <span className="access-badge">{hasAccess ? "Access" : "No access"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* --- Report Configuration Summary (Add-ons) --- */}
        {activeData?.reportAddons && activeData.reportAddons.length > 0 && (
          <div className="config-section mt-4">
            <div className="section-title">
              <FaLayerGroup className="text-success" />
              Report Configuration Summary
            </div>
            <div className="tags-container">
              {activeData.reportAddons.map((addon, i) => (
                <div className="feature-tag active" key={i}>
                  <FaCheckCircle size={12} /> {addon}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default HomePage;
